import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { loadView, viewQuery } from "@/api/views";
import { brandHead } from "@/lib/head";
import PortfolioPage from "@/pages/app/portfolio/[id]/page";
import PortfolioNewPage from "@/pages/app/portfolio/new/page";
import PortfoliosPage from "@/pages/app/portfolio/page";

import { appRoute, APP_BOUNDARIES } from "./w0b";

interface PortfoliosSearch {
  q?: string;
  published?: string;
  page?: string;
}

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다(값 판정은 core 가 한다).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

const portfoliosQuery = (search: PortfoliosSearch) => viewQuery("portfolios", search);

const PortfoliosRouteComponent = () => {
  const search = portfoliosRoute.useSearch();
  const { data } = useSuspenseQuery(portfoliosQuery(search));
  return <PortfoliosPage data={data} />;
};

export const portfoliosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolio",
  validateSearch: (search: Record<string, unknown>): PortfoliosSearch => ({ q: textOf(search.q), published: textOf(search.published), page: textOf(search.page) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, portfoliosQuery(deps)),
  head: () => brandHead("시공사례"),
  component: PortfoliosRouteComponent,
  ...APP_BOUNDARIES,
});

const PortfolioNewRouteComponent = () => {
  const { data } = useSuspenseQuery(viewQuery("portfolio-new"));
  return <PortfolioNewPage data={data} />;
};

export const portfolioNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolio/new",
  loader: ({ context }) => loadView(context.queryClient, viewQuery("portfolio-new")),
  head: () => brandHead("시공사례 등록"),
  component: PortfolioNewRouteComponent,
  ...APP_BOUNDARIES,
});

const PortfolioRouteComponent = () => {
  const { id } = portfolioRoute.useParams();
  const { data } = useSuspenseQuery(viewQuery("portfolio", { id }));
  return <PortfolioPage data={data} />;
};

export const portfolioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolio/$id",
  loader: ({ context, params }) => loadView(context.queryClient, viewQuery("portfolio", { id: params.id })),
  // 제목은 읽어 둔 사례 이름 — 없으면 「시공사례」.
  head: ({ match, params }) => {
    const title = match.context.queryClient.getQueryData(viewQuery("portfolio", { id: params.id }).queryKey)?.title;
    return brandHead(title ? `시공사례 ${title}` : "시공사례");
  },
  component: PortfolioRouteComponent,
  ...APP_BOUNDARIES,
});

// g4a 배치의 라우트 — 회원 시공사례 목록·등록·편집.
export const routes: AnyRoute[] = [portfoliosRoute, portfolioNewRoute, portfolioRoute];
